import { useRouter, useRoute } from "vue-router";

/**
 * Provides a smart "go back" function that uses real browser history when
 * available, and falls back to a sensible catalog URL otherwise.
 *
 * Rules (per project navigation spec):
 * - Use real router history first (router.back()).
 * - If no valid previous route exists (direct URL, refresh, new tab),
 *   fall back to the product's category page or the main catalog.
 * - Never fake browser history or hardcode a single URL.
 */
export default function useSmartBack() {
  const router = useRouter();
  const route = useRoute();

  /**
   * Detect whether real internal history exists.
   * Vue Router stores a `back` property on `window.history.state` when there
   * is a previous route in its own navigation stack.
   */
  const hasInternalHistory = (): boolean => {
    if (typeof window === "undefined") return false;
    const state = window.history.state;
    // Vue Router sets `state.back` to the previous path when navigating
    return !!(state && state.back);
  };

  /**
   * Build a fallback URL from available context:
   * 1. `category_id` query param → /store/category/:id
   * 2. Saved catalog URL in sessionStorage (set by catalog page on leave)
   * 3. Main catalog page "/"
   */
  const getFallbackUrl = (): string => {
    // Check if product page was opened with a category context
    const categoryId = route.query.category_id as string | undefined;
    if (categoryId) {
      return `/store/category/${categoryId}`;
    }

    // Check for a saved catalog URL (set before navigating away from catalog)
    if (typeof sessionStorage !== "undefined") {
      const savedCatalogUrl = sessionStorage.getItem("vf_last_catalog_url");
      if (savedCatalogUrl) {
        return savedCatalogUrl;
      }
    }

    // Ultimate fallback: main page
    return "/";
  };

  const isCatalogUrl = (url: string): boolean => {
    return (
      url === "/" ||
      url.startsWith("/?") ||
      url.startsWith("/store/category/") ||
      url.startsWith("/store/search") ||
      url.startsWith("/store/manufacturer/")
    );
  };

  const markCatalogReturn = (url?: string) => {
    if (typeof sessionStorage === "undefined") return;

    const targetUrl = url || window.history.state?.back || "";
    if (targetUrl && !isCatalogUrl(String(targetUrl))) return;

    sessionStorage.setItem("vf_back", "1");
  };

  /**
   * Navigate back: use real history if available, otherwise push fallback.
   */
  const goBack = () => {
    if (hasInternalHistory()) {
      markCatalogReturn();
      router.back();
    } else {
      const fallback = getFallbackUrl();
      markCatalogReturn(fallback);
      router.push(fallback);
    }
  };

  return {
    goBack,
    hasInternalHistory,
    getFallbackUrl,
  };
}
